import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSectorReturns } from '../services/sectorBreadthService';
import type { SectorReturns } from '../services/sectorBreadthService';
import { SECTORS } from '../constants/sectors';
import type { Sector } from '../constants/sectors';
import { PanelMessage } from './PanelMessage';

/**
 * Sector Rotation — S&P 500 sector performance via the SPDR select-sector ETFs.
 *
 * Ported from the Duck Terminal `rotation.py` / `rotation_result.html` tool, so
 * the thresholds, weights and quadrant meanings match what that already
 * produces. Differences are deliberate and noted inline.
 *
 * ⚠️ **The returns are QuantForge's, not ours.** This panel used to derive every
 * horizon from ~400 days of daily closes; QF now serves the finished figures,
 * because the same numbers are rendered on their own surface and two
 * independent derivations is how the two disagree about what "1W" means. What
 * stays client code is everything below the data: thresholds, quadrant
 * meanings, colours and bubble weights are presentation decisions.
 *
 * ⚠️ **One request for all eleven sectors.** The reference implementation used
 * `useQueries` to fan out one call per sector — eleven requests per browser,
 * per window, against a provider allowing three concurrent per ACCOUNT. QF
 * serves this from their own store with no vendor call on the request path.
 */

/* ── palette ────────────────────────────────────────────────────────────
   Duck used #ce3a5f / #b03050 for the negative end. Swapped to the brand
   Down/Put red so this panel matches everything around it.              */

const C = {
  leading: '#2494cc',
  advancing: '#1c76a2',
  flat: '#6b7280',
  declining: '#d8483b',
  // Brighter than DECLINING, mirroring LEADING against ADVANCING: on both
  // sides the extreme is the intense tone and the near band is the faded one.
  // This was inverted — LAGGING sat darker, so the worst sectors read as the
  // quietest ones.
  lagging: '#e85848',
  cooling: '#c2a14e',
  uptrend: '#1c76a2',
  downtrend: '#d8483b',
  relief: '#d8783b',
};

function strength(pct: number | null): { label: string; color: string } {
  if (pct == null) return { label: '—', color: C.flat };
  if (pct >= 1.0) return { label: 'LEADING', color: C.leading };
  if (pct >= 0.25) return { label: 'ADVANCING', color: C.advancing };
  if (pct > -0.25) return { label: 'FLAT', color: C.flat };
  if (pct > -1.0) return { label: 'DECLINING', color: C.declining };
  return { label: 'LAGGING', color: C.lagging };
}

function pctColor(v: number | null): string {
  if (v == null) return C.flat;
  if (v >= 1.0) return C.leading;
  if (v >= 0.1) return C.advancing;
  if (v > -0.1) return C.flat;
  if (v > -1.0) return C.declining;
  return C.lagging;
}

/** Heat tint for a table cell. Stepped, not continuous — steps stay legible. */
function cellBg(v: number | null): string | undefined {
  if (v == null) return undefined;
  if (v >= 3.0) return 'rgba(64,184,232,0.13)';
  if (v >= 1.5) return 'rgba(36,148,204,0.09)';
  if (v >= 0.5) return 'rgba(28,118,162,0.06)';
  if (v >= 0.1) return 'rgba(28,118,162,0.03)';
  if (v <= -3.0) return 'rgba(232,88,72,0.14)';
  if (v <= -1.5) return 'rgba(216,72,59,0.10)';
  if (v <= -0.5) return 'rgba(216,72,59,0.06)';
  if (v <= -0.1) return 'rgba(216,72,59,0.03)';
  return undefined;
}

function fmtPct(v: number | null): string {
  if (v == null) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

/* ── returns ────────────────────────────────────────────────────────── */

interface SectorRow extends Sector {
  price: number | null;
  chg1d: number | null;
  /** The scatter's Y axis. QF's `ret_5d`, which is what "5D Return" plots. */
  chg5d: number | null;
  chg1w: number | null;
  chg1m: number | null;
  chg3m: number | null;
  chg6m: number | null;
  ytd: number | null;
}

/**
 * One table row from QF's returns, or an all-dash row when a sector is absent.
 *
 * ⚠️ **Name and weight come from OUR `SECTORS`, not the response.** QF sends
 * both, but `sectors.ts` is shared with the other panels on this page — taking
 * QF's wording would let one panel say "Consumer Cyclical" while its neighbour
 * says "Consumer Discretionary" for the same sector. QF's own note calls
 * `weight` cosmetic, and ours already drives bubble size.
 *
 * ⚠️ Values pass through unscaled: QF returns PERCENT (`+1.48` is +1.48%), and
 * `fmtPct` appends the sign and the symbol. Multiplying by 100 here would give
 * a 148% day.
 */
function buildRow(sector: Sector, r: SectorReturns | undefined): SectorRow {
  return {
    ...sector,
    price: r?.last ?? null,
    chg1d: r?.ret_1d ?? null,
    chg5d: r?.ret_5d ?? null,
    chg1w: r?.ret_1w ?? null,
    chg1m: r?.ret_1m ?? null,
    chg3m: r?.ret_3m ?? null,
    chg6m: r?.ret_6m ?? null,
    ytd: r?.ret_ytd ?? null,
  };
}

/* ── panels ─────────────────────────────────────────────────────────── */

function Panel({ title, meta, className = '', children }: {
  title: string;
  meta?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className={`p-4 ${className}`}>
      <div className="flex items-baseline justify-between gap-3 mb-3 flex-none">
        <h3 className="text-[10px] uppercase tracking-wider text-gray-400">{title}</h3>
        {meta}
      </div>
      {children}
    </section>
  );
}

function PerformanceBars({ rows }: { rows: SectorRow[] }): React.ReactElement {
  const maxAbs = Math.max(...rows.map((r) => Math.abs(r.chg1d ?? 0)), 0.01);

  return (
    <Panel title="1D Performance" className="flex flex-col">
      <div className="flex-1 flex flex-col justify-between gap-1.5 min-h-0">
        {rows.map((r) => {
          const { label, color } = strength(r.chg1d);
          const width = (Math.abs(r.chg1d ?? 0) / maxAbs) * 100;
          return (
            <div key={r.sym} className="flex items-center gap-3 text-xs">
              <span
                className="w-[74px] flex-none text-center text-[9px] font-bold uppercase tracking-wider rounded px-1 py-0.5"
                style={{ color, background: `${color}1a`, border: `1px solid ${color}44` }}
              >
                {label}
              </span>
              <span className="w-10 flex-none font-semibold text-accent-yellow">{r.sym}</span>
              <span className="flex-1 min-w-0 truncate text-gray-200">{r.name}</span>
              <span className="w-[35%] flex-none">
                {/* Bars share one scale so lengths compare across rows. */}
                <span className="block h-[5px] rounded-sm" style={{ width: `${width}%`, background: color, opacity: 0.85 }} />
              </span>
              <span className="w-16 flex-none text-right font-numeric" style={{ color }}>
                {fmtPct(r.chg1d)}
              </span>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

/** Corner label naming a quadrant of the scatter. */
function Badge({ text, color, style }: {
  text: string;
  color: string;
  style: React.CSSProperties;
}): React.ReactElement {
  return (
    <span
      className="absolute inline-block rounded px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider"
      style={{ color, background: `${color}18`, border: `1px solid ${color}44`, ...style }}
    >
      {text}
    </span>
  );
}

function Scatter({ rows }: { rows: SectorRow[] }): React.ReactElement {
  // Symmetric ranges with a little padding, so the zero lines stay centred and
  // the quadrants keep their meaning.
  const rangeX = Math.max(...rows.map((r) => Math.abs(r.chg1d ?? 0)), 1) + 0.25;
  // ⚠️ `chg5d`, not `chg1w`. These were one field when the client derived both
  // from closes — "1W" WAS five sessions. QF sends `ret_1d`/`ret_5d` as the
  // quadrant axes and `ret_1w` as a table column, and they are not guaranteed
  // equal. The axis is labelled "5D Return", so it plots the 5D figure.
  const rangeY = Math.max(...rows.map((r) => Math.abs(r.chg5d ?? 0)), 1) + 0.5;
  const maxWeight = Math.max(...rows.map((r) => r.weight));

  const ticks = (range: number) => {
    const step = range <= 1 ? 0.25 : range <= 2.5 ? 0.5 : 1;
    const n = Math.floor(range / step);
    return Array.from({ length: 2 * n + 1 }, (_, i) => (i - n) * step);
  };

  return (
    <Panel
      title="1D vs 5D Return"
      meta={
        <div className="flex items-center gap-3 text-[9px] text-gray-400">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: C.advancing }} /> up today
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full" style={{ background: C.declining }} /> down today
          </span>
          <span>size = weight</span>
        </div>
      }
    >
      <div className="relative" style={{ height: 300 }}>
        {/* Plot area. The margins are the axis gutters: ml-12 leaves room for
            the rotated Y title plus its tick labels, mb-9 keeps the X title
            clear of the tick row rather than landing on top of "0%". */}
        <div className="absolute inset-0 ml-12 mb-9">
          {ticks(rangeY).map((t) => (
            <div key={`y${t}`} className="absolute left-0 right-0 flex items-center"
              style={{ top: `${50 - (t / rangeY) * 44}%` }}>
              <span className="absolute -left-7 -translate-y-1/2 text-[8px] text-gray-400 font-numeric w-6 text-right">
                {t >= 0 ? '+' : ''}{t.toFixed(t % 1 ? 1 : 0)}%
              </span>
              <div className="w-full h-px" style={{ background: t === 0 ? '#333' : '#1e1e1e' }} />
            </div>
          ))}
          {ticks(rangeX).map((t) => (
            <div key={`x${t}`} className="absolute top-0 bottom-0"
              style={{ left: `${50 + (t / rangeX) * 44}%` }}>
              <div className="h-full w-px" style={{ background: t === 0 ? '#333' : '#1e1e1e' }} />
              <span className="absolute -bottom-4 -translate-x-1/2 text-[8px] text-gray-400 font-numeric whitespace-nowrap">
                {t >= 0 ? '+' : ''}{t.toFixed(t % 1 ? 1 : 0)}%
              </span>
            </div>
          ))}

          {/* Quadrants. Up on both axes is an uptrend; up today after a weak
              week is relief; down today after a strong week is cooling. */}
          <Badge text="Cooling" color={C.cooling} style={{ top: 2, left: 2 }} />
          <Badge text="Uptrend" color={C.uptrend} style={{ top: 2, right: 2 }} />
          <Badge text="Downtrend" color={C.downtrend} style={{ bottom: 2, left: 2 }} />
          <Badge text="Relief" color={C.relief} style={{ bottom: 2, right: 2 }} />

          {rows.map((r) => {
            const x = 50 + ((r.chg1d ?? 0) / rangeX) * 44;
            const y = 50 - ((r.chg5d ?? 0) / rangeY) * 44;
            const d = 9 + (r.weight / maxWeight) * 17;
            const color = (r.chg1d ?? 0) >= 0 ? C.advancing : C.declining;
            return (
              <div key={r.sym} className="absolute" style={{ left: `${x}%`, top: `${y}%` }}>
                <span
                  className="block rounded-full -translate-x-1/2 -translate-y-1/2"
                  style={{ width: d, height: d, background: color, opacity: 0.85 }}
                />
                <span
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-[9px] font-semibold whitespace-nowrap"
                  style={{ color }}
                >
                  {r.sym}
                </span>
              </div>
            );
          })}
        </div>

        {/* Axis titles. Y was missing entirely, which left the vertical scale
            unnamed on a chart whose whole point is the pairing. */}
        <div className="absolute bottom-0 left-12 right-0 text-center text-[8px] uppercase tracking-wider text-gray-400">
          1D Return
        </div>
        {/* The rotation lives on the inner span, not the flex box. Putting
            writing-mode on the container swaps its main axis, so items-center
            no longer centres along the height and the label sinks to one end. */}
        <div className="absolute left-0 top-0 bottom-9 flex items-center justify-center">
          <span
            className="text-[8px] uppercase tracking-wider text-gray-400 whitespace-nowrap"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            5D Return
          </span>
        </div>
      </div>
    </Panel>
  );
}

const COLS: [keyof SectorRow, string][] = [
  ['chg1d', '1D'],
  ['chg1w', '1W'],
  ['chg1m', '1M'],
  ['chg3m', '3M'],
  ['chg6m', '6M'],
  ['ytd', 'YTD'],
];

function PerformanceTable({ rows }: { rows: SectorRow[] }): React.ReactElement {
  return (
    <Panel title="Performance" meta={<span className="text-[9px] text-gray-400">sorted by 1D</span>}>
      <div className="overflow-x-auto rounded border border-dark-border">
        <table className="w-full text-xs">
          <thead className="bg-dark-surface-light border-b border-dark-border">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-gray-400">Sector</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-400">ETF</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-400 border-l border-dark-border">Price</th>
              {COLS.map(([key, label]) => (
                <th key={key} className="px-3 py-2 text-right font-semibold text-gray-400 border-l border-dark-border">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sym} className="border-b border-dark-border/40 hover:bg-dark-surface-light transition-colors">
                <td className="px-3 py-2 text-gray-200 whitespace-nowrap">{r.name}</td>
                <td className="px-3 py-2 font-semibold text-accent-yellow whitespace-nowrap">
                  {r.sym}
                </td>
                <td className="px-3 py-2 text-right font-numeric text-white border-l border-dark-border">
                  {r.price != null ? r.price.toFixed(2) : '—'}
                </td>
                {COLS.map(([key]) => {
                  const v = r[key] as number | null;
                  return (
                    <td
                      key={key}
                      className="px-3 py-2 text-right font-numeric border-l border-dark-border"
                      style={{ color: pctColor(v), background: cellBg(v) }}
                    >
                      {fmtPct(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/* ── composition ────────────────────────────────────────────────────── */

export function SectorRotation(): React.ReactElement {
  const { data, isLoading } = useQuery({
    queryKey: ['sectorReturns'],
    queryFn: fetchSectorReturns,
    // No date in the key: QF resolves the session itself and returns the figures
    // for it, so there is no window for the client to pin. The old key carried a
    // `from` date computed from `todayEt()` precisely because the client chose
    // the window; that responsibility has moved.
    //
    // Fifteen minutes matches the horizon columns, which only change on
    // completed sessions. `ret_1d` moves intraday and is the reason this is not
    // longer.
    staleTime: 15 * 60 * 1000,
  });

  // Every sector, sorted by 1D with any that failed to load last. Sectors with
  // no data are kept rather than filtered — a sector quietly disappearing
  // reads as "there are only nine sectors", which is worse than an em dash.
  const rows = useMemo(
    () =>
      SECTORS.map((s) => buildRow(s, data?.[s.sym])).sort((a, b) => {
        if ((a.price == null) !== (b.price == null)) return a.price == null ? 1 : -1;
        return (b.chg1d ?? 0) - (a.chg1d ?? 0);
      }),
    [data]
  );

  // The bars and the scatter plot positions, so they can only show sectors
  // that have one.
  const plotted = useMemo(() => rows.filter((r) => r.price != null), [rows]);

  return (
    /* One card for the whole section. The three modules below are different
       views of the same sectors, so boxing each one separately implied they
       were unrelated panels that happened to sit together. */
    <section className="rounded-lg border border-dark-border bg-dark-surface">
      {/* Rule under the title, same as the panel headers elsewhere. Spans the
          full card width so it lines up with the module dividers below it. */}
      <div className="px-4 py-3 border-b border-dark-border">
        <h2 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Sector Rotation</h2>
      </div>

      {isLoading && !plotted.length ? (
        <div className="px-4">
          <PanelMessage tone="loading">Loading sector data&hellip;</PanelMessage>
        </div>
      ) : !plotted.length ? (
        <div className="px-4">
          {/* No explanation, deliberately: the endpoint is live, so a failure
              here is a real fault rather than a missing feature, and a stale
              reassurance is worse than none. */}
          <PanelMessage tone="unavailable">
            Sector rotation unavailable
          </PanelMessage>
        </div>
      ) : (
        <>
          {/* Hairlines between the modules, not gaps — they divide one card
              rather than separating several. */}
          <div className="grid grid-cols-1 xl:grid-cols-2 divide-y xl:divide-y-0 xl:divide-x divide-dark-border">
            <PerformanceBars rows={plotted} />
            <Scatter rows={plotted} />
          </div>
          <div className="border-t border-dark-border">
            <PerformanceTable rows={rows} />
          </div>
        </>
      )}
    </section>
  );
}
